// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyPasswordDetails = { name: string; password: string };

type Props = { onClose: () => void; details?: unknown };

/**
 * The one moment the password exists outside a hash. Closing this is the end of it — getting it back
 * means issuing a new one, which is only possible while they have never set their own.
 */
export default function PasswordPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { name, password } = details as TyPasswordDetails;
    const [isCopied, setIsCopied] = useState(false);

    /**
     * `navigator.clipboard` exists only in a secure context — over `https` or on `localhost`. Opening
     * the dev server on a phone through the LAN address is neither, so it is missing exactly when it is
     * most wanted. The textarea fallback is the old way, and it works anywhere.
     */
    const handleCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(password);
            } else {
                const field = document.createElement("textarea");
                field.value = password;
                field.setAttribute("readonly", "");
                field.style.position = "fixed";
                field.style.opacity = "0";
                document.body.appendChild(field);
                field.select();
                document.execCommand("copy");
                document.body.removeChild(field);
            }
            setIsCopied(true);
        } catch {
            setIsCopied(false);
        }
    };

    return (
        <section className="popup-card password-popup">
            <h2 className="password-popup__title">{t.team.passwordTitle}</h2>
            <p className="password-popup__hint">{t.team.passwordHint.replace("{name}", name)}</p>

            <div className="password-popup__value">
                <code dir="ltr">{password}</code>
                <button type="button" className="password-popup__copy" onClick={handleCopy}>
                    <Icon name="copy" size={18} />
                    <span>{isCopied ? t.common.copied : t.common.copy}</span>
                </button>
            </div>

            <button type="button" className="password-popup__done" onClick={onClose}>
                {t.common.done}
            </button>
        </section>
    );
}
